import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, TrendingUp, AlertTriangle, CheckCircle, Mail, Eye, 
  RefreshCw, Download, Search, Filter, Calendar, Gift,
  Stethoscope, Clock, UserCheck, Send, QrCode
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

const UTILIZATION_COLORS = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-orange-100 text-orange-800',
  never: 'bg-red-100 text-red-800'
};

const FulfillmentDashboard = () => {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [validationCode, setValidationCode] = useState('');
  const [validationBenefitId, setValidationBenefitId] = useState('');
  const [exporting, setExporting] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fulfillment/clinic/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadMembers = useCallback(async () => {
    try {
      let url = `${API}/fulfillment/clinic/members?limit=100`;
      if (filter !== 'all') {
        url += `&utilization_filter=${filter}`;
      }
      const response = await axios.get(url);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadStats();
    loadMembers();
  }, [loadStats, loadMembers]);

  const handleViewDetails = async (member) => {
    setSelectedMember(member);
    try {
      const response = await axios.get(`${API}/fulfillment/clinic/member/${member.member_id}`);
      setMemberDetails(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load member details');
    }
  };

  const handleSendReminder = async () => {
    if (!selectedMember) return;
    
    try {
      await axios.post(`${API}/fulfillment/clinic/send-reminder`, {
        member_id: selectedMember.member_id,
        reminder_type: 'underutilization',
        custom_message: reminderMessage || undefined
      });
      toast.success(`Reminder sent to ${selectedMember.member_name}`);
      setShowReminderDialog(false);
      setReminderMessage('');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleValidateBenefit = async () => {
    if (!validationCode || !validationBenefitId) {
      toast.error('Please enter both validation code and benefit ID');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/fulfillment/clinic/validate-benefit?validation_code=${validationCode}&benefit_id=${validationBenefitId}`);
      toast.success(`Benefit validated for ${response.data.member_name}`);
      setShowValidateDialog(false);
      setValidationCode('');
      setValidationBenefitId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid validation code');
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/fulfillment/clinic/export?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fulfillment_report_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      } else {
        console.log('Report data:', response.data);
        toast.success('Report generated');
      }
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const getUtilizationBadge = (percentage) => {
    if (percentage > 80) return <Badge className={UTILIZATION_COLORS.high}>High ({percentage}%)</Badge>;
    if (percentage >= 50) return <Badge className={UTILIZATION_COLORS.medium}>Medium ({percentage}%)</Badge>;
    if (percentage > 0) return <Badge className={UTILIZATION_COLORS.low}>Low ({percentage}%)</Badge>;
    return <Badge className={UTILIZATION_COLORS.never}>Never Used</Badge>;
  };

  const filteredMembers = members.filter(m => 
    m.member_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Membership Fulfillment</h1>
          <p className="text-gray-600">Track and manage member service utilization</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowValidateDialog(true)}>
            <QrCode className="h-4 w-4 mr-2" />
            Validate Benefit
          </Button>
          <Button variant="outline" onClick={() => handleExport('csv')} disabled={exporting}>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button onClick={() => { loadStats(); loadMembers(); }} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_members}</p>
                  <p className="text-xs text-gray-600">Total Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.avg_utilization}%</p>
                  <p className="text-xs text-gray-600">Avg Utilization</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.high_utilizers}</p>
                <p className="text-xs text-gray-600">High Usage (&gt;80%)</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.medium_utilizers + stats.low_utilizers}</p>
                <p className="text-xs text-gray-600">Medium/Low</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.never_used}</p>
                  <p className="text-xs text-gray-600">Never Used</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter and Search */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle>Member Fulfillment</CardTitle>
              <CardDescription>Monitor service usage and identify members needing attention</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  <SelectItem value="high">High Usage</SelectItem>
                  <SelectItem value="medium">Medium Usage</SelectItem>
                  <SelectItem value="low">Low Usage</SelectItem>
                  <SelectItem value="never">Never Used</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Utilization</TableHead>
                <TableHead>Services</TableHead>
                <TableHead>Last Visit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No members found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredMembers.map((member) => (
                  <TableRow 
                    key={member.member_id} 
                    className={member.needs_attention ? 'bg-red-50' : ''}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{member.member_name}</p>
                        <p className="text-xs text-gray-500">{member.patient_id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{member.plan_name}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {getUtilizationBadge(member.utilization_percentage)}
                        <Progress value={member.utilization_percentage} className="h-1 w-20" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="font-medium">{member.services_used}</span>
                        <span className="text-gray-400">/{member.services_total}</span>
                        {member.services_pending > 0 && (
                          <span className="text-orange-600 ml-2">
                            ({member.services_pending} pending)
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {member.last_visit_date ? (
                        <div className="text-sm">
                          <p>{new Date(member.last_visit_date).toLocaleDateString()}</p>
                          {member.days_since_last_visit && (
                            <p className="text-xs text-gray-500">
                              {member.days_since_last_visit} days ago
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {member.needs_attention ? (
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="text-xs text-red-600">{member.attention_reason}</span>
                        </div>
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleViewDetails(member)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            setSelectedMember(member);
                            setShowReminderDialog(true);
                          }}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Member Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Member Fulfillment Details</DialogTitle>
            <DialogDescription>
              {memberDetails?.member_name} ({memberDetails?.patient_id})
            </DialogDescription>
          </DialogHeader>
          {memberDetails && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-teal-600">{memberDetails.overall_utilization}%</p>
                    <p className="text-xs text-gray-500">Utilization</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{memberDetails.services.length}</p>
                    <p className="text-xs text-gray-500">Services</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{memberDetails.benefits.filter(b => b.is_unlocked).length}</p>
                    <p className="text-xs text-gray-500">Active Benefits</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{memberDetails.membership_months}</p>
                    <p className="text-xs text-gray-500">Months Active</p>
                  </CardContent>
                </Card>
              </div>

              {/* Services Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Stethoscope className="h-4 w-4" />
                    Included Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service</TableHead>
                        <TableHead>Used</TableHead>
                        <TableHead>Remaining</TableHead>
                        <TableHead>Scheduled</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {memberDetails.services.map((service) => (
                        <TableRow key={service.id}>
                          <TableCell className="font-medium">{service.name}</TableCell>
                          <TableCell>{service.quantity_used}</TableCell>
                          <TableCell>
                            {service.quantity_remaining === -1 ? '∞' : service.quantity_remaining}
                          </TableCell>
                          <TableCell>{service.quantity_scheduled}</TableCell>
                          <TableCell>
                            <Badge className={
                              service.status === 'available' ? 'bg-green-100 text-green-800' :
                              service.status === 'depleted' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }>
                              {service.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Benefits */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Gift className="h-4 w-4" />
                    Benefits Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {memberDetails.benefits.map((benefit) => (
                      <div 
                        key={benefit.id} 
                        className={`p-3 rounded-lg border ${benefit.is_unlocked ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{benefit.name}</span>
                          {benefit.is_unlocked ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge variant="outline">{benefit.months_until_unlock}mo to unlock</Badge>
                          )}
                        </div>
                        {benefit.validation_code && (
                          <p className="text-xs text-gray-500 mt-1">
                            Code: <code className="bg-gray-100 px-1 rounded">{benefit.validation_code}</code>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent History */}
              {memberDetails.service_history.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Recent Service History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {memberDetails.service_history.slice(0, 5).map((record) => (
                        <div key={record.id} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span>{record.service_name}</span>
                          <span className="text-sm text-gray-500">
                            {new Date(record.usage_date).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowDetailsDialog(false);
                    setShowReminderDialog(true);
                  }}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Reminder
                </Button>
                <Button onClick={() => setShowDetailsDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Fulfillment Reminder</DialogTitle>
            <DialogDescription>
              Send a reminder to {selectedMember?.member_name} about their unused services
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Custom Message (optional)</Label>
              <Textarea
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                placeholder="Add a personalized message..."
                rows={4}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowReminderDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendReminder} className="bg-teal-600 hover:bg-teal-700">
                <Send className="h-4 w-4 mr-2" />
                Send Reminder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Validate Benefit Dialog */}
      <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Validate Benefit Code</DialogTitle>
            <DialogDescription>
              Enter the code shown by the patient to validate their benefit redemption
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Validation Code</Label>
              <Input
                value={validationCode}
                onChange={(e) => setValidationCode(e.target.value.toUpperCase())}
                placeholder="Enter 8-character code"
                className="font-mono text-lg tracking-wider"
              />
            </div>
            <div>
              <Label>Benefit ID</Label>
              <Input
                value={validationBenefitId}
                onChange={(e) => setValidationBenefitId(e.target.value)}
                placeholder="Enter benefit ID"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowValidateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleValidateBenefit} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Validate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FulfillmentDashboard;
